import type { Question } from '@api/instant_survey/types';
import {
  Card,
  IconButton,
  InfoFilledIcon,
  RadioButtonIcon,
  RepromptIcon,
  Select,
} from '@utilities';
import type { SelectOption } from '@utilities/components/Select';

import {
  getQuestionTypeLabel,
  humanizeScreenerPromptKey,
  SCREENER_HIERARCHY_OPTIONS,
} from '../../utilities';
import { renderTextWithPrompts } from './utilities/helpers';

import styles from './_index.module.scss';

interface PreviewViewProps {
  isEditing: boolean;
  missingPromptKeys: string[];
  onHierarchyChange: (option: SelectOption) => void;
  onPromptChange: (promptKey: string, value: string) => void;
  onResetPrompts: () => void;
  previewQuestion: Question;
  promptValues: Record<string, string>;
  selectedHierarchyOption: null | SelectOption;
  showHierarchySelect: boolean;
  touchedPromptKeys: Set<string>;
}

const PreviewView = ({
  isEditing,
  missingPromptKeys,
  onHierarchyChange,
  onPromptChange,
  onResetPrompts,
  previewQuestion,
  promptValues,
  selectedHierarchyOption,
  showHierarchySelect,
  touchedPromptKeys,
}: PreviewViewProps) => (
  <>
    <div className={styles['preview-view-banner']}>
      <InfoFilledIcon />
      <span>
        <strong>This is a preview for a Numerator screener.</strong> Screener questions have limited
        edits and do not count toward your total.
      </span>
    </div>
    {showHierarchySelect && (
      <div className={styles['preview-view-controls']}>
        <Select
          data-testid="screener-hierarchy-select"
          label="Hierarchy"
          onChange={(option) => option && onHierarchyChange(option as SelectOption)}
          options={SCREENER_HIERARCHY_OPTIONS}
          value={selectedHierarchyOption}
        />
      </div>
    )}
    <Card className={styles['preview-view-card']} padding="large">
      <div className={styles['preview-view-card-header']}>
        <span className={styles['preview-view-type']}>
          S1 · {getQuestionTypeLabel(previewQuestion.type).toUpperCase()}
        </span>
        <IconButton
          data-testid="screener-reset-prompts"
          onClick={onResetPrompts}
          tooltip="Reset"
          type="button"
        >
          <RepromptIcon />
        </IconButton>
      </div>
      <p className={styles['preview-view-question']}>
        {renderTextWithPrompts({
          keyPrefix: 'question',
          missingPromptKeys,
          onPromptChange,
          promptValues,
          template: previewQuestion.text,
          touchedPromptKeys,
        })}
      </p>
      <div className={styles['preview-view-answers']}>
        {previewQuestion.answers.map(({ id, text }) => (
          <span key={id}>
            <RadioButtonIcon />
            <span>
              {renderTextWithPrompts({
                keyPrefix: `answer-${id}`,
                missingPromptKeys,
                onPromptChange,
                promptValues,
                template: text,
                touchedPromptKeys,
              })}
            </span>
          </span>
        ))}
      </div>
      {missingPromptKeys.length > 0 && (
        <p className={styles['preview-view-error']} data-testid="screener-missing-prompts-message">
          Fill all editable prompts before {isEditing ? 'applying changes' : 'adding this screener'}
          : {missingPromptKeys.map(humanizeScreenerPromptKey).join(', ')}.
        </p>
      )}
    </Card>
  </>
);

export default PreviewView;
